import { Children, useEffect, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { DotMenuIcon, CopyIcon, DownloadIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import type { WithChildren } from '../../../core/types/with-children.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { devLog } from '../../../core/utils/logging.js';
import { useCopyToClipboard } from '../../../core/utils/useCopyToClipboard.js';
import { Menu } from '../../../navigation/menu/Menu.js';
import { actionButtonContainer } from '../CodeSnippet.sty.js';
import { CODESNIPPET_COMPONENT_NAME } from '../constants.js';
import type { HighlightLanguage } from '../HighlightExtended.js';
import { codeSnippetActionsMenuMessages as messages } from './actions-menu-messages.js';
import { Copy, CopyInternal, type CodeSnippetCopyProps } from './Copy.js';
import {
  Download,
  DownloadInternal,
  downloadSnippet,
  type CodeSnippetDownloadProps,
} from './Download.js';

/**
 * Slot component that enables the actions menu in `CodeSnippet`.
 *
 * Whichever single one of `CodeSnippet.Copy` or `CodeSnippet.Download`
 * is present renders as a single button matching that action.
 * Having both present escalates the UI to a menu trigger.
 * @public
 */
export const ActionsMenu = (props: WithChildren): null => null;
(ActionsMenu as typeof ActionsMenu & { displayName: string }).displayName =
  'CodeSnippet.ActionsMenu';

/**
 * Which of the `CodeSnippet` actions are enabled, and how each one is configured.
 * An absent entry means that action isn't rendered.
 * @internal
 */
export interface ActionsMenuConfig {
  /**
   * Configuration of the Copy action, when enabled.
   */
  copyConfig?: CodeSnippetCopyProps;
  /**
   * Configuration of the Download action, when enabled.
   */
  downloadConfig?: CodeSnippetDownloadProps;
}

/**
 * Extracts the recognized `CodeSnippet.Copy` and `CodeSnippet.Download` children of a
 * `CodeSnippet.ActionsMenu`.
 * Any other child is dropped with a dev warning, and a repeated slot keeps the last occurrence.
 * @param children - The children of the `CodeSnippet.ActionsMenu` slot element.
 */
function extractActionsMenuConfig(children: ReactNode): ActionsMenuConfig {
  const config: ActionsMenuConfig = {};
  for (const child of Children.toArray(children)) {
    if (isSlot(child, Copy)) {
      config.copyConfig = child.props;
    } else if (isSlot(child, Download)) {
      config.downloadConfig = child.props;
    } else {
      devLog(
        'Only CodeSnippet.Copy and CodeSnippet.Download are supported as children of CodeSnippet.ActionsMenu.',
      );
    }
  }
  return config;
}

// DEPRECATION: [APPDEV-16570] BREAKING-CHANGE: reword TSDoc to have no mention of deprecated props.
/**
 * Returns the configuration carried by the `CodeSnippet.ActionsMenu` slot among
 * `children`, or `undefined` when the slot isn't used at all. The distinction
 * matters: an unused slot falls back to the deprecated `allowCopy`/`showCopyAction` handling.
 * @param children - The children of the `CodeSnippet` itself.
 * @internal
 */
export function getActionsMenuSlotConfig(
  children: ReactNode,
): ActionsMenuConfig | undefined {
  let config: ActionsMenuConfig | undefined;
  Children.forEach(children, (child) => {
    if (isSlot(child, ActionsMenu)) {
      config = extractActionsMenuConfig(child.props.children);
    }
  });

  return config;
}

/**
 * Props for the `ActionsMenuInternal` component.
 * @internal
 */
export interface ActionsMenuInternalProps {
  /**
   * Which actions to render, and how each one is configured.
   */
  config: ActionsMenuConfig;
  /**
   * Layout size of the snippet, matched by the action buttons.
   */
  size: 'default' | 'condensed';
  /**
   * The snippet's plain-text content, copied to the clipboard or written to the
   * downloaded file.
   */
  snippetContent: string;
  /**
   * The snippet's highlight language, used to derive the downloaded file's extension.
   */
  language?: HighlightLanguage;
}

/**
 * Renders what `CodeSnippet.ActionsMenu` was configured with:
 * - A menu when both Copy and Download are present,
 * - A single button when only one of them is,
 * - Nothing when empty.
 * @internal
 */
export const ActionsMenuInternal = ({
  config: { copyConfig, downloadConfig },
  size,
  snippetContent,
  language,
}: ActionsMenuInternalProps) => {
  const intl = useIntl();
  const [copyToClipboard, copied, setCopied] =
    useCopyToClipboard(snippetContent);

  useEffect(() => {
    if (copied) {
      copyConfig?.onCopy?.();
      setCopied(false);
    }
  }, [copied, copyConfig, setCopied]);

  if (!copyConfig && !downloadConfig) {
    return null;
  }

  return (
    <div className={actionButtonContainer({ size })}>
      {copyConfig && downloadConfig ? (
        <Menu data-dt-component={CODESNIPPET_COMPONENT_NAME}>
          <Menu.Trigger>
            <Button
              variant="default"
              size={size}
              aria-label={intl.formatMessage(messages.triggerLabel)}
              data-dt-children-name="OpenActionsMenu"
              data-dt-component={CODESNIPPET_COMPONENT_NAME}
            >
              <Button.Prefix>
                <DotMenuIcon />
              </Button.Prefix>
            </Button>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Group>
              <Menu.Item
                onSelect={() => copyToClipboard()}
                data-dt-children-name="Copy"
              >
                <Menu.Prefix>
                  <CopyIcon />
                </Menu.Prefix>
                {intl.formatMessage(messages.copy)}
              </Menu.Item>
              <Menu.Item
                onSelect={() =>
                  downloadSnippet(snippetContent, downloadConfig, language)
                }
                data-dt-children-name="Download"
              >
                <Menu.Prefix>
                  <DownloadIcon />
                </Menu.Prefix>
                {intl.formatMessage(messages.download)}
              </Menu.Item>
            </Menu.Group>
          </Menu.Content>
        </Menu>
      ) : null}
      {copyConfig && !downloadConfig ? (
        <CopyInternal
          size={size}
          copyText={snippetContent}
          onCopy={copyConfig.onCopy}
        />
      ) : null}
      {!copyConfig && downloadConfig ? (
        <DownloadInternal
          size={size}
          onSelect={() =>
            downloadSnippet(snippetContent, downloadConfig, language)
          }
        />
      ) : null}
    </div>
  );
};
